// Catalog.ID Client — The bits sequences
// ============================================================================
// Every step that moves a voucher's bits (registration-wallets §4.4, §4.5),
// gathered in one module because five surfaces run pieces of them: both
// registration wizards, the delegate activation wizard, the storefront's
// personalize section and the organization's Delegates pane.
//
// ── the rule that shapes every signature here ───────────────────────────────
// A PRINT STEP IS NEVER BLOCKED BY THE MONEY LEG (§4.5, §10). The kit is the
// account's only durable copy of its credentials; a Worker that is down, a lock
// whose condition passed its `notAfter`, a rate limiter that has had enough —
// none of those may stand between a member and their printed credentials. So
// nothing in this module throws on a Worker refusal. Every function returns an
// OUTCOME, and the caller renders it beside the kit rather than instead of it.
//
// The one exception is {@link findFreeParkingSlot}, which is a reclaim action a
// person deliberately clicked: it has no kit behind it, and a failure there is
// worth an error.
//
// ── why retries are safe ────────────────────────────────────────────────────
// A release drains its lock and closes it with `releasedTo`. A second release
// to the SAME target answers 200 with the same body — that is the idempotent
// retry the whole lane is built on, and it is why a client that crashed between
// the Worker call and its own bookkeeping simply calls again. A release to a
// DIFFERENT target after the first succeeded answers `released`, and that is a
// bug in the caller, never a retry.

import { signLockRelease, lockKeyFor, type LockCondition } from '../crypto/bitcash';
import type { RegistrationType } from '../api/payment';
import {
    lockErrorCode, LOCK_NO_LIVE_CONDITION, LOCK_EXPIRED, LOCK_UNKNOWN, type BitcashWorker,
} from '../api/bitcash-worker';
import { bitToUnits } from '../api/bitcash-units';
import { verifiedRelease } from '../api/ledger-rows';
import type { NetworkId } from '@catalog-bitcash-code/format';
import type { DefaultTokenTerms } from './payment-flow';

// ── the relock (§4.4) ──────────────────────────────────────────────────────

/**
 * D28. The invitation condition outlives the CID expiry by thirty days, so a
 * registration begun on the invitation's last day still funds its print step.
 * The two clocks are deliberately different: the CID expiry gates the
 * REGISTRATION, `notAfter` gates the MONEY.
 */
export const INVITATION_LOCK_GRACE_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * The two conditions an invitation lock carries (§4.4 step 3), in the order the
 * §4.15 vector pins:
 *
 *   1. the INVITEE, with `notAfter` = the CID expiry + 30 days;
 *   2. the BUYER, with no `notAfter` at all — the reclaim path never dies, so a
 *      voucher handed to somebody who never registers comes back (§4.4's
 *      "Reclaim"), and the only thing that ends it is the lock's own 5-year
 *      term.
 *
 * The order is part of the signature: the Worker hashes the array as it is
 * given, and the client signs that hash. Swapping the two entries would produce
 * a descriptor the Worker rebuilds differently and refuse with `bad_signature`.
 */
export function invitationLockConditions(
    invitationPublicKeyHex: string,
    voucherPublicKeyHex: string,
    cidExpiresAt: string | Date,
): LockCondition[] {
    const expiry = typeof cidExpiresAt === 'string' ? Date.parse(cidExpiresAt) : cidExpiresAt.getTime();
    if (!Number.isFinite(expiry)) throw new Error('The invitation expiry is not a date');
    return [
        { alg: 'ed25519', publicKey: invitationPublicKeyHex, notAfter: expiry + INVITATION_LOCK_GRACE_MS },
        { alg: 'ed25519', publicKey: voucherPublicKeyHex },
    ];
}

/** One leg of a relock: drain this lock into that one. */
export interface RelockLeg {
    /** Compact form. */
    fromLockKey: string;
    target: { kind: 'lock'; key: string; conditions: LockCondition[] };
}

/**
 * Every lock a personalize has to move (§4.4, §4.13). An organization voucher
 * carries TWO — the account lock and the founder lock — and both must land on
 * the SAME slots of the invitation key, or the invitee's wizard would look for
 * the founder's money at slot 0 and find the organization's.
 *
 * Deterministic in its arguments, which is what makes a Worker retry safe: the
 * second attempt computes the same lock keys and the same conditions, so it
 * meets the idempotent 200 rather than creating anything twice.
 */
export function relockPlanFor(args: {
    /** The voucher's two lock keys and the public half they derive from — the
     *  subset of a minted voucher a relock needs, named structurally so this
     *  module does not depend on the storefront. */
    voucher: { lockKey: string; founderLockKey?: string; publicKeyHex: string };
    invitation: { publicKey: Uint8Array; publicKeyHex: string };
    registrationType: RegistrationType;
    expiresAt: string | Date;
}): RelockLeg[] {
    const conditions = invitationLockConditions(
        args.invitation.publicKeyHex, args.voucher.publicKeyHex, args.expiresAt);

    const legs: RelockLeg[] = [{
        fromLockKey: args.voucher.lockKey,
        target: { kind: 'lock', key: lockKeyFor(args.invitation.publicKey, 0), conditions },
    }];

    if (args.registrationType === 'organization') {
        if (!args.voucher.founderLockKey) {
            throw new Error('An organization voucher must carry a founder lock (slot 1) to personalize');
        }
        legs.push({
            fromLockKey: args.voucher.founderLockKey,
            target: { kind: 'lock', key: lockKeyFor(args.invitation.publicKey, 1), conditions },
        });
    }
    return legs;
}

/** The one thing every sequence needs. Injected so no unit test reaches the
 *  network — every path in this module is a LIVE path. */
export interface BitsDeps {
    worker: BitcashWorker;
}

/** §4.12: the label the default token carries, and the one the key-grant
 *  handler family matches when it adopts an existing token instead of minting a
 *  second one. Changing it orphans every token already issued. */
export const DEFAULT_TOKEN_LABEL = 'catalog:default';

/** What §4.5 tells the member when the money is gone but the account is fine. */
const EXPIRED_MESSAGE = 'The bits attached to this code have expired, so none arrived in your '
    + 'wallet. Everything else about your account is complete — ask the person who invited you '
    + 'if you were expecting them.';

const UNAVAILABLE_MESSAGE = 'Your bits could not be collected just now. Your account and your '
    + 'wallet are complete; the bits stay where they are and can be collected later.';

/** A lock the ledger does not know: the code was minted without a gift (`--no-bits`, the
 *  pane's gift box unticked, a node that gifts nothing). There is nothing to collect, ever,
 *  and the wallet does not exist yet — a v2 wallet is created by its first credit. */
const NO_GIFT_MESSAGE = 'This code carries no bits, so none arrived and your wallet does not exist on the ledger yet: '
    + 'it is created by its first credit of at least 1,000 BIT. Your account is complete, and your printed '
    + 'wallet key is the one that credit will open.';

/** The release answered, but its signed row did not confirm it (apps/bitcash/docs/plans/bitcash-v2.md M12). */
const UNVERIFIED_MESSAGE = 'The ledger reported your bits as collected, but its signed record of that could not be '
    + 'checked, so no amount is shown here. Your account and your wallet are complete; open your wallet later to see what it holds.';

/** The outcome code of a release whose signed row did not verify. */
export const RELEASE_UNVERIFIED = 'unverified';

const ALREADY_RELEASED_MESSAGE = 'The bits on this code were already collected into a different '
    + 'wallet. Nothing is wrong with your account — ask whoever gave you the code if you were '
    + 'expecting them here.';

/** The outcome of one attempt to move bits into a wallet. */
export type BitsReleaseOutcome =
    /** `amount`: units, decimal string (1 BIT = 10⁸ units). */
    | { status: 'released'; walletRef: string; amount: string }
    /** The condition or the lock passed its deadline. Nothing to retry. */
    | { status: 'expired'; message: string }
    /** Anything else — a Worker outage, a rate limit, a lock that went
     *  elsewhere. `code` is the §4.2 machine code when there was one. */
    | { status: 'unavailable'; code: string | null; message: string };

/** Classify a thrown Worker error into the two non-success outcomes. */
function classify(err: unknown): BitsReleaseOutcome {
    const code = lockErrorCode(err);
    if (code === LOCK_NO_LIVE_CONDITION || code === LOCK_EXPIRED) {
        return { status: 'expired', message: EXPIRED_MESSAGE };
    }
    if (code === 'released') {
        return { status: 'unavailable', code, message: ALREADY_RELEASED_MESSAGE };
    }
    if (code === LOCK_UNKNOWN) {
        return { status: 'unavailable', code, message: NO_GIFT_MESSAGE };
    }
    return { status: 'unavailable', code, message: UNAVAILABLE_MESSAGE };
}

/**
 * §4.5: drain a lock straight into the wallet the ceremony minted.
 *
 * `seed` is the Ed25519 seed derived from whichever code opens this lock — the
 * VOUCHER's on the self-use path, the INVITATION's on the invitee path. The
 * target is a CASH key the ledger has never seen, so the release creates the
 * wallet and funds it in one step. The member's OWN browser makes this request,
 * straight to the ledger — never through the CID node — because a wallet
 * released from an invitation lock takes the request's country (D56;
 * open-questions item 48) and the pair (issuance wallet, 1 y) (D70).
 */
export async function releaseBitsToWallet(
    args: { lockKey: string; walletKey: string; seed: Uint8Array },
    deps: BitsDeps,
): Promise<BitsReleaseOutcome> {
    const target = { kind: 'cash' as const, key: args.walletKey };
    try {
        const signature = signLockRelease(args.seed, args.lockKey, target);
        const { walletRef, amount } = await deps.worker.releaseLock({
            lockKey: args.lockKey, target, signature,
        });
        return { status: 'released', walletRef, amount };
    } catch (err) {
        return classify(err);
    }
}

/** The outcome of a relock (§4.4 step 3). */
export type RelockOutcome =
    | { status: 'relocked' }
    /** Some legs moved and some did not. `pending` is exactly what a retry
     *  should attempt; the legs that moved answer the idempotent 200 if it
     *  attempts them again anyway. */
    | { status: 'partial'; pending: RelockLeg[]; code: string | null; message: string };

/**
 * §4.4 step 3: move every lock of a voucher onto the invitation's keys.
 *
 * IN ORDER, and it STOPS at the first failure. The order is the account lock
 * then the founder lock (§4.13), and a caller that raced ahead after a failure
 * would have to reason about which of two locks is where. Stopping leaves one
 * fact to record: how many legs are still owed.
 *
 * The CID row is created BEFORE this runs and is not undone by a failure here:
 * an invitation whose bits are still on the voucher lock is a usable
 * invitation, and the retry moves them. The reverse order would strand bits
 * under an invitation key that names no invitation.
 */
export async function relockBits(
    legs: readonly RelockLeg[], seed: Uint8Array, deps: BitsDeps,
): Promise<RelockOutcome> {
    for (let i = 0; i < legs.length; i++) {
        const leg = legs[i];
        try {
            const signature = signLockRelease(seed, leg.fromLockKey, leg.target);
            await deps.worker.releaseLock({
                lockKey: leg.fromLockKey, target: leg.target, signature,
            });
        } catch (err) {
            const outcome = classify(err);
            return {
                status: 'partial',
                pending: legs.slice(i) as RelockLeg[],
                code: outcome.status === 'unavailable' ? outcome.code : null,
                message: outcome.status === 'expired'
                    ? 'The bits on this code have expired and could not be moved onto the invitation. '
                        + 'The invitation itself is valid; it simply carries no bits.'
                    : 'The invitation was created, but its bits could not be moved onto it yet. '
                        + 'Try again — nothing is lost, and repeating this is safe.',
            };
        }
    }
    return { status: 'relocked' };
}

/** The wallet's public receive address, or a reason there is none yet. */
export type AddressOutcome =
    | { status: 'ok'; receiveAddr: string }
    | { status: 'unavailable' };

/**
 * §4.5: bind the wallet's receive address — a FUND code generated on this
 * device and written down BEFORE this call (the ledger never returns a code,
 * T2), so a retry binds the same one. The FIRST address on a wallet is free.
 *
 * A failure prints the Bitcash Wallet sheet WITHOUT the address block rather
 * than failing the kit — the sheet is built to do that, and an address can be
 * bound later from Settings.
 */
export async function ensureReceiveAddress(
    args: { walletKey: string; fund: string }, deps: BitsDeps,
): Promise<AddressOutcome> {
    try {
        await deps.worker.createReceiveAddress(args.walletKey, args.fund);
        return { status: 'ok', receiveAddr: args.fund };
    } catch {
        return { status: 'unavailable' };
    }
}

/** The default token, or a reason there is none. */
export type DefaultTokenOutcome =
    | { status: 'ok'; authToken: string }
    | { status: 'unavailable' };

/**
 * §4.12, on the v2 ledger (apps/bitcash/docs/plans/bitcash-v2.md §4.16): the account's ONE default
 * token, its first and therefore free one — UNBOUND (no payee: it pays Catalog
 * services at any operator), scoped to the node's service types, no site list,
 * no expiry, the D27 caps (configured in BIT, sent in units)
 * (apps/docs/plans/second-node-and-bitcash-open-issues.md §2.1, §9.3). `token` is
 * a code generated on this device and written down before this call; a retry
 * re-sends it, which the ledger answers idempotently. On the v2 ledger a token
 * pays only an Operator's wallet; the service types keep this one from paying
 * content, so without them nothing is minted.
 *
 * Never throws: a failure costs one supersecret prompt the first time a module
 * asks for a token, never a registration.
 */
export async function mintDefaultToken(
    args: { walletKey: string; token: string; dailyCap: number; monthlyCap: number; serviceTypes: readonly string[] },
    deps: BitsDeps,
): Promise<DefaultTokenOutcome> {
    if (args.serviceTypes.length === 0) return { status: 'unavailable' };
    try {
        await deps.worker.createToken({
            walletKey: args.walletKey,
            token: args.token,
            caps: { day: bitToUnits(args.dailyCap), month: bitToUnits(args.monthlyCap), serviceTypes: [...args.serviceTypes] },
        });
        return { status: 'ok', authToken: args.token };
    } catch {
        return { status: 'unavailable' };
    }
}

/**
 * Slot numbering (§4.1): 0 is the account lock, 1 is the founder lock on
 * organization vouchers and invitations, and PARKING LOCKS START AT 2.
 */
export const FIRST_PARKING_SLOT = 2;

/** How far the probe walks before giving up. A voucher that has been reclaimed
 *  sixty times is a bug or an attack, not a member. */
const MAX_PARKING_SLOT = FIRST_PARKING_SLOT + 62;

/**
 * §4.4's reclaim target: the first parking slot of `publicKey` that names no
 * lock yet, probed upward from 2.
 *
 * A CLOSED lock is NOT free — its key exists on the Worker, and a release
 * targeting it would be refused for naming an existing key. Only `exists:
 * false` is free.
 *
 * RACE (§10): two devices reclaiming the same voucher at once can pick the same
 * `n`; the loser's release fails on the existing key and it probes again. That
 * is why this returns a candidate rather than a reservation.
 *
 * Throws, unlike the rest of this module: a reclaim is a deliberate click with
 * no kit behind it, so a failure is worth an error message.
 */
export async function findFreeParkingSlot(
    publicKey: Uint8Array,
    deps: BitsDeps,
    start: number = FIRST_PARKING_SLOT,
): Promise<{ slot: number; lockKey: string }> {
    for (let slot = Math.max(start, FIRST_PARKING_SLOT); slot <= MAX_PARKING_SLOT; slot++) {
        const lockKey = lockKeyFor(publicKey, slot);
        const info = await deps.worker.lockInfo(lockKey);
        if (!info.exists) return { slot, lockKey };
    }
    throw new Error('No free parking slot for this code — every slot up to '
        + `${MAX_PARKING_SLOT} already holds a lock.`);
}

/**
 * §4.4's reclaim: drain an invitation lock back into a parking lock only the
 * VOUCHER's key can open. The single condition has no `notAfter` — the buyer's
 * claim on its own money does not expire, and only the lock's own five-year
 * term ends it.
 */
export function parkingConditions(voucherPublicKeyHex: string): LockCondition[] {
    return [{ alg: 'ed25519', publicKey: voucherPublicKeyHex }];
}

// ── the print step's whole money leg (§4.5) ─────────────────────────────────

/** What the print step learned, and what the stash records. */
export interface WalletSetupResult {
    /** The release attempt, or null when there was none: no lock (no gift), or a
     *  resume whose stash already says the bits arrived. */
    release: BitsReleaseOutcome | null;
    /** The wallet's receive address — printed on the sheet, and the value the
     *  optional public claim publishes (D4). Null when it was not bound. */
    receiveAddr: string | null;
    /** The `catalog:default` token, cached on the device by the caller. Null
     *  when it was already issued, not minted, or refused. */
    defaultToken: string | null;
    /** True when the ledger holds no wallet under this key: no gift arrived, so
     *  there is nothing to bind an address or a token to yet (a v2 wallet exists
     *  only from its first credit of at least 1000 BIT). */
    walletMissing: boolean;
}

/**
 * The calls a print step makes after set-authhash (§4.5), on the v2 ledger, in
 * the one order that works:
 *
 *   1. **release** the lock into the wallet the ceremony minted. This CREATES
 *      the wallet — the CASH key has never been seen by the ledger before — so
 *      it comes first. Without a lock (no gift), or when it did not move, the
 *      ledger is asked whether the wallet exists at all (`wallet/exists`, by
 *      ref): a wallet that does not exist takes no address and no token.
 *   2. **address**: the first receive address on a wallet is free.
 *   3. **token**: so is the first token; only when the node names the default
 *      token's terms — caps and service types (it is minted unbound, §9.3).
 *
 * ── write-ahead (T2) ────────────────────────────────────────────────────────
 * `instruments` are the FUND and AUTH codes the caller generated and wrote
 * down (stash, and the account's instruments blob) BEFORE this call: the
 * ledger never returns a code, and a retry with the same code is idempotent.
 *
 * ── resume (§4.5) ───────────────────────────────────────────────────────────
 * `alreadyReleased` and `alreadyTokenIssued` come from the stash. The release
 * is idempotent at the ledger; the token flag protects a second mint, which
 * would cost a fee.
 *
 * ── and it never throws ─────────────────────────────────────────────────────
 * Each step reports its own outcome and the next one runs regardless.
 */
/**
 * What the print step says beside the kit about the money leg, or null when
 * there is nothing to say: the release's own message when the bits did not
 * arrive, and — when there was no release to speak of and the ledger holds no
 * wallet — that the code carried no bits and the wallet is created by its first
 * credit (never a promise to collect "later").
 */
export function walletSetupNotice(result: WalletSetupResult): string | null {
    if (result.release !== null && result.release.status !== 'released') return result.release.message;
    if (result.walletMissing) return NO_GIFT_MESSAGE;
    return null;
}

export async function setUpWallet(
    args: {
        /** The ceremony's CASH key, compact or display form. */
        walletKey: string;
        /** Its ref on the ledger's network (`walletRefOf`). */
        walletRef: string;
        /** The ledger's network: the release row is read with the wallet's VIEW code on it. */
        networkId: NetworkId;
        /** The lock to drain and the seed that opens it. Absent ⇒ no gift. */
        lock?: { lockKey: string; seed: Uint8Array };
        /** The default token's terms: D27's caps in BIT and the service types (§9.3 D-a); null when
         *  the node names none (go-live configuration) ⇒ no token. */
        caps: DefaultTokenTerms | null;
        /** The codes written down beforehand. */
        instruments: { fund: string; token: string };
        /** Stash: the bits already arrived (§4.5's resume rule). */
        alreadyReleased?: boolean;
        /** Stash: the default token already exists. */
        alreadyTokenIssued?: boolean;
        onStatus?: (label: string) => void;
    },
    deps: BitsDeps,
): Promise<WalletSetupResult> {
    let release: BitsReleaseOutcome | null = null;
    if (args.lock && !args.alreadyReleased) {
        args.onStatus?.('Collecting your bits…');
        release = await releaseBitsToWallet(
            { lockKey: args.lock.lockKey, walletKey: args.walletKey, seed: args.lock.seed }, deps);
        if (release.status === 'released') {
            // The release answer is unsigned: the bits count once the wallet's signed row says so.
            try {
                await verifiedRelease(deps.worker, args.walletKey, args.networkId, args.walletRef, release.amount);
            } catch (err) {
                console.warn('[bitcash] the release did not verify:', err);
                release = { status: 'unavailable', code: RELEASE_UNVERIFIED, message: UNVERIFIED_MESSAGE };
            }
        }
    }

    let exists = release?.status === 'released' || args.alreadyReleased === true;
    if (!exists) {
        try {
            exists = (await deps.worker.walletExists(args.walletRef)).exists;
        } catch {
            exists = false;
        }
    }
    if (!exists) return { release, receiveAddr: null, defaultToken: null, walletMissing: true };

    args.onStatus?.('Creating your receive address…');
    const address = await ensureReceiveAddress({ walletKey: args.walletKey, fund: args.instruments.fund }, deps);

    let defaultToken: string | null = null;
    if (!args.alreadyTokenIssued && args.caps && args.caps.serviceTypes.length > 0) {
        args.onStatus?.('Setting up payments for Catalog services…');
        const token = await mintDefaultToken({
            walletKey: args.walletKey,
            token: args.instruments.token,
            dailyCap: args.caps.dailyCap,
            monthlyCap: args.caps.monthlyCap,
            serviceTypes: args.caps.serviceTypes,
        }, deps);
        if (token.status === 'ok') defaultToken = token.authToken;
    }

    return {
        release,
        receiveAddr: address.status === 'ok' ? address.receiveAddr : null,
        defaultToken,
        walletMissing: false,
    };
}
